import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  TextField,
  Box,
  CircularProgress,
  Alert,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { APIHOST } from '../../common';
import { useAuth } from '../auth/AuthContext';
import { Order } from '../../types/order';
import { TaxInfoRequest } from '../../types/taxInfoRequest';

interface EditOrderTaxesProps {
  open: boolean;
  onClose: () => void;
  orderId: string;
  onSave?: () => void;
}

const EditOrderTaxes: React.FC<EditOrderTaxesProps> = ({
  open,
  onClose,
  orderId,
  onSave
}) => {
  const { userToken } = useAuth();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [invoicedTaxRatePercentage, setInvoicedTaxRatePercentage] = useState('');
  const [invoicedTaxPerRoomNight, setInvoicedTaxPerRoomNight] = useState('');

  // Load order data when dialog opens
  useEffect(() => {
    if (open && orderId) {
      loadOrder();
    }
  }, [open, orderId]);

  const loadOrder = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${APIHOST}/api/orders/${orderId}`, {
        headers: {
          'Authorization': `Bearer ${userToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to load order: ${response.status}`);
      }

      const data = await response.json();
      setOrder(data.data);
      
      // Set form values
      setInvoicedTaxRatePercentage(data.data.invoicedTaxRatePercentage?.toString() || '');
      setInvoicedTaxPerRoomNight(data.data.invoicedTaxPerRoomNight?.toString() || '');
    } catch (err) {
      console.error('Error loading order:', err);
      setError(err instanceof Error ? err.message : 'Failed to load order');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');

    try {
      const updateData: TaxInfoRequest = {
        data: {
          invoicedTaxRatePercentage: invoicedTaxRatePercentage ? parseFloat(invoicedTaxRatePercentage) : 0,
          invoicedTaxPerRoomNight: invoicedTaxPerRoomNight ? parseFloat(invoicedTaxPerRoomNight) : 0
        }
      };

      const response = await fetch(`${APIHOST}/api/orders/${orderId}/tax-info`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${userToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        throw new Error(`Failed to update order: ${response.status}`);
      }

      // Call onSave callback if provided
      if (onSave) {
        onSave();
      }
      
      onClose();
    } catch (err) {
      console.error('Error saving order:', err);
      setError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setError('');
    onClose();
  };

  const isValidNumber = (value: string) => {
    return value === '' || (!isNaN(parseFloat(value)) && isFinite(parseFloat(value)));
  };

  const isSaveDisabled = () => {
    return saving || 
           !isValidNumber(invoicedTaxRatePercentage) || 
           !isValidNumber(invoicedTaxPerRoomNight);
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { minHeight: '300px' }
      }}
    >
      <DialogTitle>
        Edit Taxes & Fees
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {!loading && order && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            <TextField
              label="Tax Rate Percentage"
              type="number"
              value={invoicedTaxRatePercentage}
              onChange={(e) => setInvoicedTaxRatePercentage(e.target.value)}
              fullWidth
              inputProps={{ 
                step: 0.01,
                min: 0 
              }}
              error={!isValidNumber(invoicedTaxRatePercentage)}
              helperText={!isValidNumber(invoicedTaxRatePercentage) ? 'Please enter a valid number' : 'Enter the tax rate as a percentage'}
            />
            
            <TextField
              label="Tax Per Room Night"
              type="number"
              value={invoicedTaxPerRoomNight}
              onChange={(e) => setInvoicedTaxPerRoomNight(e.target.value)}
              fullWidth
              inputProps={{ 
                step: 0.01,
                min: 0 
              }}
              error={!isValidNumber(invoicedTaxPerRoomNight)}
              helperText={!isValidNumber(invoicedTaxPerRoomNight) ? 'Please enter a valid number' : 'Enter the tax amount per room night'}
            />
            
            <Typography variant="body2" color="text.secondary">
              These values will be used for invoice calculations. Make sure to regenerate the invoice after making changes.
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} color="secondary">
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          color="primary"
          disabled={isSaveDisabled()}
        >
          {saving ? <CircularProgress size={20} /> : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditOrderTaxes;